import '.d{display:flex;}.ssr-plugin'
import '.e{flex-direction:column;}.ssr-plugin'
import '.v{background-color:var(--palette-white);}.ssr-plugin'
import '.js{border-radius:var(--border-radius-image);}.ssr-plugin'
import '.jt{padding:3.5rem 2rem;}.ssr-plugin'
import '.t{align-items:center;}.ssr-plugin'
import '.bm{text-align:center;}.ssr-plugin'
import '.n{width:100%;}.ssr-plugin'
import '.bg{height:100%;}.ssr-plugin'
import '.is{box-sizing:border-box;}.ssr-plugin'
import '.ju{width:12.5rem;}.ssr-plugin'
import '.a{display:grid;}.ssr-plugin'
import '.jv{grid-template-rows:auto 1fr auto;}.ssr-plugin'
import '.bd{flex:1;}.ssr-plugin'
import '.ax{justify-content:center;}.ssr-plugin'
import '.jw{padding:0.75rem 0;}.ssr-plugin'
import '.ap{justify-content:flex-end;}.ssr-plugin'
import '.s{flex-direction:row;}.ssr-plugin'
import '.g{gap:1rem;}.ssr-plugin'
import '.bc{flex-shrink:0;}.ssr-plugin'
import '.aq{position:relative;}.ssr-plugin'
import '.jx{transition:transform 0.25s ease, box-shadow 0.25s ease;}.ssr-plugin'
import '.jy{border:var(--card-border);}.ssr-plugin'
import '.jz{background-color:lightgray;}.ssr-plugin'
import '.k0{background-size:100px 100px, auto;}.ssr-plugin'
import '.k1{background-repeat:repeat, no-repeat;}.ssr-plugin'
import '.k2{background-position:0% 0%, 0% 0%;}.ssr-plugin'
import '.k3{background-blend-mode:overlay, normal;}.ssr-plugin'
import { Button } from '@app/components/core/button/Button'
import { Image } from '@app/components/core/image/Image'
import { Paragraph } from '@app/components/core/typography/Paragraph'
import { Title } from '@app/components/core/typography/Title'
import useMediaQuery from '@app/util/useMediaQuery'
import contactCardTexture from '@app/assets/images/background-contact-card.png'
import { type Style, styled } from 'inlines'
import { SafeHtml } from '@app/components/core/safeHtml/SafeHtml'
import Column from '@app/components/layout/Column'

export type ContactPerson = {
  imageSrc: string
  imageAlt: string
  name: string
  role: string
  buttonLabel: string
  buttonLink: string
}

export type ContactCardProps = {
  title: string
  description?: string
  htmlContent?: string
  contactPeople: ContactPerson[]
  style?: Style
}

const CONTACT_PERSON_CARD_WIDTH = '16.5rem'
const STACK_PEEK_AT_THREE = '10rem'
const STACK_PEEK_AT_FIVE = '3rem'
const STACK_COUNT_FOR_WIDEST_PEEK = 3
const STACK_COUNT_FOR_TIGHTEST_PEEK = 5

const getHorizontalStackPeek = (count: number): string => {
  if (count <= STACK_COUNT_FOR_WIDEST_PEEK) {
    return STACK_PEEK_AT_THREE
  }

  if (count >= STACK_COUNT_FOR_TIGHTEST_PEEK) {
    return STACK_PEEK_AT_FIVE
  }

  const minPeek = parseFloat(STACK_PEEK_AT_THREE)
  const maxPeek = parseFloat(STACK_PEEK_AT_FIVE)
  const progress =
    (count - STACK_COUNT_FOR_WIDEST_PEEK) /
    (STACK_COUNT_FOR_TIGHTEST_PEEK - STACK_COUNT_FOR_WIDEST_PEEK)
  const peek = minPeek + progress * (maxPeek - minPeek)

  return `${peek}rem`
}

const getHorizontalStackOverlap = (peek: string) =>
  `(${CONTACT_PERSON_CARD_WIDTH} - ${peek})`

const ContactPersonCard = ({
  person,
  style,
}: {
  person: ContactPerson
  style?: Style
}) => (
  <styled.div className="d e v js jt t bm n bg is"
    style={{
      ...style,
    }}
  >
    <Image
      src={person.imageSrc}
      alt={person.imageAlt}
      resizeWidth={144}
      style={{
        width: '9rem',
        height: '9rem',
        borderRadius: '50%',
        objectFit: 'cover',
        objectPosition: 'center top',
        marginBottom: '0.75rem',
      }}
    />
    <div className="ju a jv bd"
    >
      <Title.H4
        style={{
          whiteSpace: 'pre-line',
          marginBottom: 0,
        }}
      >
        {person.name}
      </Title.H4>
      <div className="d t ax bm jw"
      >
        <Paragraph.Medium
          style={{
            marginBottom: 0,
          }}
        >
          {person.role}
        </Paragraph.Medium>
      </div>
      {person.buttonLabel.trim() ? (
        <Button
          size="medium"
          variant="primary"
          label={person.buttonLabel}
          to={person.buttonLink || undefined}
        />
      ) : null}
    </div>
  </styled.div>
)

const ContactPeopleLayout = ({
  contactPeople,
  isMobile,
}: {
  contactPeople: ContactPerson[]
  isMobile: boolean
}) => {
  const count = contactPeople.length

  if (count === 0) {
    return null
  }

  if (isMobile) {
    return (
      <Column>
        {contactPeople.map((person, index) => (
          <ContactPersonCard
            key={`${index}-${person.name}`}
            person={person}
          />
        ))}
      </Column>
    )
  }

  if (count === 1) {
    return (
      <div className="d ap"
        style={{
          width: CONTACT_PERSON_CARD_WIDTH,
        }}
      >
        <ContactPersonCard person={contactPeople[0]} />
      </div>
    )
  }

  if (count === 2) {
    return (
      <div className="d s g ap"
      >
        {contactPeople.map((person, index) => (
          <div className="bc"
            key={`${index}-${person.name}`}
            style={{
              width: CONTACT_PERSON_CARD_WIDTH,}}
          >
            <ContactPersonCard person={person} />
          </div>
        ))}
      </div>
    )
  }

  const stackPeek = getHorizontalStackPeek(count)
  const stackOverlap = getHorizontalStackOverlap(stackPeek)

  return (
    <div className="d s ap"
      style={{
        width: `calc(${CONTACT_PERSON_CARD_WIDTH} + ${count - 1} * ${stackPeek})`,
      }}
    >
      {contactPeople.map((person, index) => (
        <styled.div className="aq bc jx js jy"
          key={`${index}-${person.name}`}
          style={{
            width: CONTACT_PERSON_CARD_WIDTH,
            marginLeft: index === 0 ? 0 : `calc(-1 * ${stackOverlap})`,
            zIndex: index + 1,
            '&:hover': {
              zIndex: count + 1,
              transform: 'translateY(-0.5rem)',
              boxShadow: '0 12px 32px rgba(0, 0, 0, 0.18)',
            },
          }}
        >
          <ContactPersonCard person={person} />
        </styled.div>
      ))}
    </div>
  )
}

export const ContactCard = ({
  title,
  description,
  htmlContent,
  style,
  contactPeople,
}: ContactCardProps) => {
  const isMobile = useMediaQuery()

  return (
    <styled.div className="bg jz k0 k1 k2 k3"
      style={{
        backgroundImage: `url(${contactCardTexture}), linear-gradient(177deg, #FFF 13.41%, #FBEBD1 66.89%, #FFCB93 93.89%, #F1A782 115.68%)`,
        borderRadius: isMobile ? '0.5rem' : '3rem',
        padding: isMobile ? '0.5rem' : '4.5rem',
        ...style,
      }}
    >
      <div className="a t"
        style={{
          gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) auto',
          gap: isMobile ? '2rem' : '9rem',}}
      >
        <styled.div>
          {title && <Title.H2>{title}</Title.H2>}
          {description && <Paragraph.Medium>{description}</Paragraph.Medium>}
          {htmlContent && <SafeHtml html={htmlContent} />}
        </styled.div>

        <ContactPeopleLayout
          contactPeople={contactPeople}
          isMobile={isMobile}
        />
      </div>
    </styled.div>
  )
}
